import { defineStore } from 'pinia'

export type ThemeId = 'violet'

export const DEFAULT_THEME: ThemeId = 'violet'

interface ThemeState {
  theme: ThemeId
  darkMode: boolean
}

/**
 * One brand theme since the V3 convergence (issue #40) retired teal-breeze /
 * indigo-sky / site-sync. Any value left in `si-theme` (including a retired id)
 * resolves to the brand default, and `init()` rewrites the stored value — so a
 * future theme change needs no compatibility whitelist.
 */
function getInitialTheme(): ThemeId {
  return DEFAULT_THEME
}

function getInitialDarkMode(): boolean {
  const stored = localStorage.getItem('si-dark-mode')
  if (stored !== null) return stored === 'true'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const useThemeStore = defineStore('theme', {
  state: (): ThemeState => ({
    theme: getInitialTheme(),
    darkMode: getInitialDarkMode(),
  }),

  actions: {
    toggleDarkMode() {
      this.darkMode = !this.darkMode
      localStorage.setItem('si-dark-mode', String(this.darkMode))
      document.documentElement.classList.toggle('dark', this.darkMode)
    },

    init() {
      // Rewrite a retired/unknown stored id so the next boot starts clean.
      localStorage.setItem('si-theme', this.theme)
      document.documentElement.setAttribute('data-theme', this.theme)
      document.documentElement.classList.toggle('dark', this.darkMode)
    },
  },
})
